// src/api/progressApi.ts
import { ModuleProgress, CourseProgress, ExerciseProgress } from "@nicsmith/shared_types";
import { baseApi } from "./api";

// Type for next exercise response
export interface NextExerciseResult {
  exercise_slug: string;
  module_slug: string;
  course_slug: string;
  has_progress: boolean;
}

// Type for user progress summary
export interface UserProgressSummary {
  courses_enrolled: number;
  courses_completed: number;
  exercises_completed: number;
  total_time_spent: number; // seconds
}

// Type for student dashboard aggregated data
export interface DashboardData {
  weekly_activity: Array<{ date: string; label: string; count: number }>;
  heatmap: Record<string, { count: number; modules_completed: number }>;
  exercise_status: { completed: number; in_progress: number; not_started: number; total: number };
}

export const progressApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch all exercise progress for the authenticated user
    getAllExerciseProgress: builder.query<ExerciseProgress[], void>({
      query: () => ({
        url: `/progress/exercises`,
      }),
      providesTags: ["Course"],
    }),

    // Fetch module progress by module ID
    getModuleProgressByModuleId: builder.query<ModuleProgress, string>({
      query: (moduleId) => ({
        url: `/progress/module/${moduleId}`,
      }),
    }),

    // Fetch all module progress for a course (for the authenticated user)
    getModuleProgressByCourse: builder.query<ModuleProgress[], string>({
      query: (courseId) => ({
        url: `/progress/module/course/${courseId}`,
      }),
      providesTags: ["Course"],
    }),

    // ===== Course progress =====
    getUserCourseProgress: builder.query<CourseProgress[], void>({
      query: () => ({
        url: `/progress/course`,
      }),
      providesTags: ["Course"],
    }),

    getUserCourseProgressByCourse: builder.query<CourseProgress, string>({
      query: (courseId) => ({
        url: `/progress/course/${courseId}`,
      }),
      providesTags: ["Course"],
    }),

    enrollInCourse: builder.mutation<CourseProgress, string>({
      query: (courseId) => ({
        url: `/progress/course/${courseId}`,
        method: "POST",
      }),
      invalidatesTags: ["Course"],
    }),

    // ===== Dashboard =====
    getStudentDashboardData: builder.query<DashboardData, void>({
      query: () => ({
        url: `/progress/dashboard`,
      }),
      providesTags: ["Course"],
    }),

    // ===== Next exercise (course-wide) =====
    getNextExercise: builder.query<NextExerciseResult | null, string>({
      query: (courseId) => ({
        url: `/progress/course/${courseId}/next-exercise`,
      }),
      providesTags: ["Course"],
    }),

    // ===== User stats =====
    getUserProgressSummary: builder.query<UserProgressSummary, void>({
      query: () => ({
        url: `/progress/summary`,
      }),
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in components
export const {
  useGetAllExerciseProgressQuery,
  useGetModuleProgressByModuleIdQuery,
  useGetModuleProgressByCourseQuery,
  useGetUserCourseProgressQuery,
  useGetUserCourseProgressByCourseQuery,
  useEnrollInCourseMutation,
  useGetNextExerciseQuery,
  useGetStudentDashboardDataQuery,
  useGetUserProgressSummaryQuery,
} = progressApi;
